import { useContext } from 'react';
import TrackWrapper from '@/common/TrackWrapper';
import SectionWrapper from '@/home/common/SectionWrapper';
import { FinanceCard, TradeCard } from '@/home/ReturnUser/HeroCards/Cards';
import {
  FinanceTradeWrapper,
  GridWrapper,
  HeroFavoriteAndSearchCard
} from '@/home/ReturnUser/HeroCards/HeroCards.styles';
import SRPLinks from '@/home/ReturnUser/HeroCards/LinkCards/SRPLinks';
import VDPLinks from '@/home/ReturnUser/HeroCards/LinkCards/VDPLinks';
import OrderState from '~/constants/common/OrderState';
import { vexBlobUrl } from '~/constants/environment';
import { useConsumer } from '~/context/common/StaticContext';
import { useGetUser } from '~/context/common/UserContext';
import UserPurchaseContext from '~/context/home/UserPurchaseContext';
import { PurchaseEventPayload } from '~/native/common/contracts';
import { useSourcedHandleNativePurchaseOnClick } from '~/native/common/hooks';
import {
  getNativeVdpPayload,
  nativeMyCarsPayload,
  nativeOpdPayload
} from '~/native/common/utils';
import formatEvent from '~/utilities/formatEvent';
import KeepShoppingBand from './KeepShoppingBand';
import OrderStatusCard from './OrderStatusCard';
import { getOrderStatusCardConfig } from './utils';
import VehicleHeroImage from './VehicleHeroImage';

export default function OrderStatusModule() {
  const {
    userPurchase: {
      data: { openVerifications, orderState, vehicle }
    }
  } = useContext(UserPurchaseContext);
  const consumer = useConsumer();
  const user = useGetUser();
  const handleNativePurchaseOnClick = useSourcedHandleNativePurchaseOnClick();

  if (!vehicle) return null;

  const {
    imageUrl: vehicleImageUrl,
    inventoryType,
    make,
    model,
    stockNumber,
    trim,
    vehicleId,
    year
  } = vehicle;

  const givenName = user?.given_name ?? '';

  const config = getOrderStatusCardConfig({
    consumer,
    givenName,
    make,
    openVerifications,
    orderState,
    vehicleId
  });

  const heroImageUrl = `${vexBlobUrl}/${stockNumber}/vdpHeliosTablet.jpg`;

  const isContinuePurchase =
    orderState === OrderState.InProgress ||
    orderState === OrderState.InProgressWithTradeIn;

  let nativePayload = '';
  switch (orderState) {
    case OrderState.OrderPlaced:
      nativePayload = nativeOpdPayload;
      break;
    case OrderState.RecentlyPurchased:
      nativePayload = nativeMyCarsPayload;
      break;
    case OrderState.Reserved:
      nativePayload = vehicleId ? getNativeVdpPayload(vehicleId) : '';
      break;
  }

  const nativePurchaseEventPayload: PurchaseEventPayload = {
    inventoryType,
    make,
    model,
    stockNumber,
    trim,
    vehicleId,
    vehicleImage: vehicleImageUrl,
    year
  };

  const customAttributes = { description: config.ctaDescription, vehicleId };

  const buildCtaProps = (ctaSource: string) => ({
    'data-analytics-attributes': JSON.stringify({
      ctaSource,
      ...customAttributes
    }),
    'data-analytics-id': 'Home feed card',
    'data-native-included': !isContinuePurchase,
    'data-native-payload': nativePayload,
    href: config.ctaLink,
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (isContinuePurchase) {
        handleNativePurchaseOnClick(event, nativePurchaseEventPayload);
      }
    }
  });

  const buttonCtaProps = buildCtaProps('button');
  const imageCtaProps = buildCtaProps('image');
  const remainingTasksCtaProps = buildCtaProps('remainingTasks');

  return (
    <div className="flex w-full flex-col bg-background-default">
      <TrackWrapper
        event={formatEvent('Home feed card', {
          action: 'viewed',
          description: 'Order Status Card',
          status: orderState,
          vehicleId
        })}
      >
        <VehicleHeroImage ctaProps={imageCtaProps} imageUrl={heroImageUrl}>
          <OrderStatusCard
            buttonCtaProps={buttonCtaProps}
            config={config}
            remainingTasksCtaProps={remainingTasksCtaProps}
            vehicle={vehicle}
          />
        </VehicleHeroImage>
      </TrackWrapper>

      <KeepShoppingBand />

      <SectionWrapper
        analyticsSection="Hero Cards"
        className="pt-24 sm:pt-4xl"
        hasSideSpacing
        hasBottomSpacing
      >
        <GridWrapper>
          <HeroFavoriteAndSearchCard>
            <VDPLinks />
          </HeroFavoriteAndSearchCard>
          <HeroFavoriteAndSearchCard>
            <SRPLinks />
          </HeroFavoriteAndSearchCard>
          <FinanceTradeWrapper>
            <FinanceCard />
            <TradeCard />
          </FinanceTradeWrapper>
        </GridWrapper>
      </SectionWrapper>
    </div>
  );
}
